import React from 'react';

import css from '../SetPlan.module.scss';
import Button from '../../../../components/UI/Button';
import PlanChecklist from '../ChecklistsAndContent/PlanChecklist';
import { LearnMoreTopic, Plan } from '../index';
import BackButton from '../../../../components/UI/BackButton';
import { PlanPrices, SavingsRewards } from '../../../../utils/prices';

interface Props {
  isMaxOrInfinityXP: boolean;
  loading: boolean | undefined;
  plans: PlanPrices;
  savings: SavingsRewards;
  onInfoClicked: (plan: Plan) => void;
  onLearnMoreClicked: (learnMoreTopic: LearnMoreTopic, plan: Plan) => void;
  onUpgradeClicked: (plan: Plan) => void;
  onBackClicked: () => void;
  analytics: any;
  flowId: string;
  promoConfig: any;
  riskFreePaintedDoorEnabled?: boolean;
}

const Plans = ({
  isMaxOrInfinityXP = false,
  onInfoClicked,
  plans,
  savings,
  loading,
  onLearnMoreClicked,
  onUpgradeClicked,
  onBackClicked,
  analytics,
  promoConfig,
  riskFreePaintedDoorEnabled
}: Props) => {
  const infinityMessaging =
    promoConfig.pricingMessageInfinity ||
    `Only $${
      plans.infinity - (isMaxOrInfinityXP ? 0 : plans.standard)
    }/month more`;
  const maxMessaging =
    promoConfig.pricingMessageMax ||
    `Only $${plans.max - (isMaxOrInfinityXP ? 0 : plans.standard)}/month more`;
  const standardMessaging =
    promoConfig.pricingMessageStandard || `Only $${plans.standard}/month`;

  const defaultCtaLabel = riskFreePaintedDoorEnabled ? 
  `Try 1 month risk-free` :
  `Try 1-month free${!isMaxOrInfinityXP ? '^' : ''}`;

  return (
    <>
      <BackButton onClick={onBackClicked} />
      <div className={css.investOption}>
        <div className={css.upgradeBadge}>
          <p>BEST VALUE</p>
        </div>
        <img
          src="../../../static/info-icon.svg"
          className={css.investOptionInfo}
          onClick={() => onInfoClicked(Plan.infinity)}
          data-testid="infinity-info"
        />
        <h3 className={css.investOptionHeader}>Greenlight Infinity&dagger;</h3>
        <p className={css.investOptionPrice}>{infinityMessaging}</p>
        <PlanChecklist
          plan="infinity"
          onLearnMoreClicked={onLearnMoreClicked}
          savings={savings}
        />
        <Button
          text={promoConfig.infinityButton || defaultCtaLabel}
          loading={loading}
          onClick={() => {
            analytics.trackEvent('Registration Invest Plan Action', {
              eventProps: {
                plan: Plan.infinity,
                price: plans.infinity,
                skipped: false
              }
            });
            onUpgradeClicked(Plan.infinity);
          }}
          data-e2e="upgrade-infinity"
        />
      </div>
      <div className={css.investOption}>
        <img
          src="../../../static/info-icon.svg"
          className={css.investOptionInfo}
          onClick={() => onInfoClicked(Plan.max)}
          data-testid="max-info"
        />
        <h3 className={css.investOptionHeader}>Greenlight Max</h3>
        <p className={css.investOptionPrice}>{maxMessaging}</p>
        <PlanChecklist
          plan="max"
          onLearnMoreClicked={onLearnMoreClicked}
          savings={savings}
        />
        <Button
          text={promoConfig.maxButton || defaultCtaLabel}
          loading={loading}
          onClick={() => {
            analytics.trackEvent('Registration Invest Plan Action', {
              eventProps: {
                plan: Plan.max,
                price: plans.max,
                skipped: false
              }
            });
            onUpgradeClicked(Plan.max);
          }}
          data-e2e="upgrade-max"
        />
      </div>
      {isMaxOrInfinityXP && (
        <div className={css.investOption}>
          <img
            src="../../../static/info-icon.svg"
            className={css.investOptionInfo}
            onClick={() => onInfoClicked(Plan.standard)}
            data-testid="standard-info"
          />
          <h3 className={css.investOptionHeader}>Greenlight Core</h3>
          <p className={css.investOptionPrice}>{standardMessaging}</p>
          <PlanChecklist
            plan="standard"
            onLearnMoreClicked={onLearnMoreClicked}
            savings={savings}
          />
          <Button
            text={promoConfig.standardButton || defaultCtaLabel}
            loading={loading}
            onClick={() => {
              analytics.trackEvent('Registration Invest Plan Action', {
                eventProps: {
                  plan: Plan.standard,
                  price: plans.standard,
                  skipped: false
                }
              });
              onUpgradeClicked(Plan.standard);
            }}
            data-e2e="upgrade-standard"
          />
        </div>
      )}
    </>
  );
};

export default Plans;
